/* =====================================================
   EIGHTEEN WELLNESS TECH — Main JavaScript
   ===================================================== */

/* --- TOAST NOTIFICATIONS --- */
class Toast {
  constructor() {
    this.el = document.getElementById('toast');
    this.timer = null;
  }
  show(message, type = 'success', duration = 3000) {
    if (!this.el) return;
    clearTimeout(this.timer);
    this.el.textContent = message;
    this.el.className = `toast toast--${type} show`;
    this.timer = setTimeout(() => this.el.classList.remove('show'), duration);
  }
}

const toast = new Toast();

/* --- HEADER --- */
class Header {
  constructor() {
    this.el = document.querySelector('.site-header');
    this.hamburger = document.querySelector('.hamburger');
    this.mobileMenu = document.querySelector('.mobile-menu');
    this.mobileClose = document.querySelector('.mobile-menu__close');
    this.overlay = document.getElementById('cart-overlay');
    this.init();
  }
  init() {
    if (!this.el) return;
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.hamburger?.addEventListener('click', () => this.openMenu());
    this.mobileClose?.addEventListener('click', () => this.closeMenu());
    this.overlay?.addEventListener('click', () => { this.closeMenu(); CartDrawer.close(); });
  }
  onScroll() {
    this.el.classList.toggle('scrolled', window.scrollY > 20);
  }
  openMenu() {
    this.mobileMenu?.classList.add('open');
    this.overlay?.classList.add('visible');
    document.body.style.overflow = 'hidden';
  }
  closeMenu() {
    this.mobileMenu?.classList.remove('open');
    this.overlay?.classList.remove('visible');
    document.body.style.overflow = '';
  }
}

/* --- CART MANAGER --- */
class CartManager {
  static async getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  static async addItem(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add item');
    }
    return res.json();
  }

  static async updateItem(lineItem, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: lineItem, quantity })
    });
    return res.json();
  }

  static async removeItem(lineItem) {
    return CartManager.updateItem(lineItem, 0);
  }

  static formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  }
}

/* --- CART DRAWER --- */
const CartDrawer = {
  el: null,
  overlay: null,

  init() {
    this.el = document.querySelector('.cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    document.querySelectorAll('[data-cart-open]').forEach(btn => btn.addEventListener('click', () => this.open()));
    document.querySelector('.cart-drawer__close')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
    this.bindEvents();
  },

  open() {
    this.el?.classList.add('open');
    this.overlay?.classList.add('visible');
    document.body.style.overflow = 'hidden';
    this.refresh();
  },

  close() {
    this.el?.classList.remove('open');
    this.overlay?.classList.remove('visible');
    document.body.style.overflow = '';
  },

  async refresh() {
    const cart = await CartManager.getCart();
    this.render(cart);
    this.updateCount(cart.item_count);
  },

  render(cart) {
    const body = this.el?.querySelector('.cart-drawer__body');
    const footer = this.el?.querySelector('.cart-drawer__footer');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg class="cart-drawer__empty-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          <p class="cart-drawer__empty-text">Your cart is empty</p>
          <p class="body-sm">Start your self-care ritual</p>
          <a href="/collections/all" class="btn btn--primary" style="margin-top:20px" onclick="CartDrawer.close()">Shop Now</a>
        </div>`;
      if (footer) footer.style.display = 'none';
      this.updateShippingBar(0);
      return;
    }

    if (footer) footer.style.display = '';
    body.innerHTML = cart.items.map((item, i) => `
      <div class="cart-item" data-line="${i + 1}">
        <img class="cart-item__img" src="${item.image ? item.image.replace('_1024x', '_160x') : ''}" alt="${item.product_title}" loading="lazy">
        <div class="cart-item__info">
          <div>
            <div class="cart-item__name">${item.product_title}</div>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          </div>
          <div class="cart-item__bottom">
            <div class="qty-control">
              <button class="qty-btn" data-action="decrease" data-line="${i + 1}" aria-label="Decrease">−</button>
              <input class="qty-input" type="number" value="${item.quantity}" min="1" data-line="${i + 1}" aria-label="Quantity">
              <button class="qty-btn" data-action="increase" data-line="${i + 1}" aria-label="Increase">+</button>
            </div>
            <div class="cart-item__price">${CartManager.formatMoney(item.final_line_price)}</div>
          </div>
          <button class="cart-item__remove" data-line="${i + 1}">Remove</button>
        </div>
      </div>`).join('');

    const subtotalEl = this.el?.querySelector('.cart-drawer__subtotal-price');
    if (subtotalEl) subtotalEl.textContent = CartManager.formatMoney(cart.total_price);
    const checkoutBtn = this.el?.querySelector('.cart-drawer__checkout-btn');
    if (checkoutBtn) checkoutBtn.textContent = `Checkout — ${CartManager.formatMoney(cart.total_price)}`;
    this.updateShippingBar(cart.total_price);
    this.bindCartEvents();
    cartUpsell.update(cart.items);
  },

  bindCartEvents() {
    this.el?.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const line = parseInt(btn.dataset.line);
        const input = this.el.querySelector(`.qty-input[data-line="${line}"]`);
        const current = parseInt(input.value);
        const newQty = btn.dataset.action === 'increase' ? current + 1 : Math.max(0, current - 1);
        input.value = newQty;
        const cart = await CartManager.updateItem(line, newQty);
        this.render(cart);
        this.updateCount(cart.item_count);
      });
    });
    this.el?.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        const cart = await CartManager.removeItem(parseInt(btn.dataset.line));
        this.render(cart);
        this.updateCount(cart.item_count);
      });
    });
  },

  updateCount(count) {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = count;
      el.classList.toggle('visible', count > 0);
    });
    document.querySelectorAll('.cart-title-count').forEach(el => {
      el.textContent = `(${count})`;
    });
  },

  updateShippingBar(totalPrice) {
    const bar = document.getElementById('shipping-bar');
    if (!bar) return;
    const threshold = parseInt(bar.dataset.threshold) || 5000;
    const remaining = threshold - totalPrice;
    const progress = Math.min(100, Math.round(totalPrice * 100 / threshold));
    const fill = bar.querySelector('.cart-shipping-bar__fill');
    const text = bar.querySelector('.cart-shipping-bar__text');
    if (fill) fill.style.width = progress + '%';
    if (text) {
      if (remaining > 0) {
        text.className = 'cart-shipping-bar__text';
        text.innerHTML = `You're <strong>${CartManager.formatMoney(remaining)}</strong> away from <strong>free shipping</strong>`;
      } else {
        text.className = 'cart-shipping-bar__text cart-shipping-bar__text--success';
        text.innerHTML = '🎉 You\'ve unlocked <strong>free shipping!</strong>';
      }
    }
  },

  bindEvents() {
    this.refresh();
  }
};

/* --- ADD TO CART HANDLER --- */
class AddToCart {
  constructor(form) {
    this.form = form;
    this.btn = form.querySelector('.atc-btn');
    this.init();
  }
  init() {
    this.form.addEventListener('submit', e => this.onSubmit(e));
  }
  async onSubmit(e) {
    e.preventDefault();
    const variantId = this.form.querySelector('[name="id"]')?.value;
    const qty = parseInt(this.form.querySelector('.qty-input')?.value || 1);
    if (!variantId) return;

    this.btn.classList.add('btn--loading');
    this.btn.disabled = true;

    try {
      await CartManager.addItem(variantId, qty);
      CartDrawer.open();
      toast.show('Added to cart', 'success');

      if (window.ttq) ttq.track('AddToCart');
      if (window.fbq) fbq('track', 'AddToCart');
    } catch (err) {
      toast.show(err.message || 'Could not add item', 'error');
    } finally {
      this.btn.classList.remove('btn--loading');
      this.btn.disabled = false;
    }
  }
}

/* --- STICKY ATC BAR --- */
class StickyATC {
  constructor() {
    this.bar = document.querySelector('.sticky-atc');
    this.trigger = document.querySelector('.product-info__atc');
    if (!this.bar || !this.trigger) return;
    this.observer = new IntersectionObserver(entries => {
      this.bar.classList.toggle('visible', !entries[0].isIntersecting);
    }, { threshold: 0, rootMargin: '0px' });
    this.observer.observe(this.trigger);
    const btn = this.bar.querySelector('.atc-btn');
    btn?.addEventListener('click', () => {
      document.querySelector('.product-info__atc .atc-btn')?.click();
    });
  }
}

/* --- PRODUCT GALLERY --- */
class ProductGallery {
  constructor(el) {
    this.el = el;
    this.main = el.querySelector('.product-gallery__main');
    this.mainImg = el.querySelector('.product-gallery__main img');
    this.thumbs = el.querySelectorAll('.product-gallery__thumb');
    this.current = 0;
    this.init();
  }
  init() {
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setActive(i));
    });
    this.main?.addEventListener('click', () => this.zoom());
    this.initSwipe();
  }
  initSwipe() {
    let startX = 0;
    this.main?.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
    }, { passive: true });
    this.main?.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < 40) return;
      if (dx < 0) {
        this.setActive((this.current + 1) % this.thumbs.length);
      } else {
        this.setActive((this.current - 1 + this.thumbs.length) % this.thumbs.length);
      }
    }, { passive: true });
  }
  setActive(index) {
    this.current = index;
    const src = this.thumbs[index]?.querySelector('img')?.src;
    if (this.mainImg && src) {
      const srcLarge = src.replace(/width=\d+/, 'width=900');
      this.mainImg.removeAttribute('srcset');
      this.mainImg.src = srcLarge;
    }
    this.thumbs.forEach((t, i) => t.classList.toggle('active', i === index));
  }
  zoom() {
    this.lbImages = Array.from(this.thumbs).map(t => t.dataset.zoomSrc || '').filter(Boolean);
    if (!this.lbImages.length) return;
    this.lbIndex = this.current;
    this.openLightbox();
  }
  openLightbox() {
    const lb = document.getElementById('img-lightbox');
    const lbImg = document.getElementById('img-lightbox-img');
    const dots = document.getElementById('img-lightbox-dots');
    if (!lb || !lbImg) return;
    const currentUrl = this.lbImages[this.lbIndex];
    lbImg.style.backgroundImage = 'url(' + currentUrl + ')';
    lbImg.style.backgroundColor = currentUrl.includes('.png') ? '#fff' : 'transparent';
    lb.style.display = 'block';
    if (dots) {
      dots.innerHTML = this.lbImages.map((_, i) =>
        `<span style="width:7px;height:7px;border-radius:50%;background:${i === this.lbIndex ? '#fff' : 'rgba(255,255,255,0.35)'};display:inline-block;"></span>`
      ).join('');
    }
    if (!lb._lbReady) {
      lb._lbReady = true;
      document.getElementById('img-lightbox-close')?.addEventListener('click', () => { lb.style.display = 'none'; });
      document.getElementById('img-lightbox-prev')?.addEventListener('click', () => { this.lbIndex = (this.lbIndex - 1 + this.lbImages.length) % this.lbImages.length; this.openLightbox(); });
      document.getElementById('img-lightbox-next')?.addEventListener('click', () => { this.lbIndex = (this.lbIndex + 1) % this.lbImages.length; this.openLightbox(); });
      lb.addEventListener('click', e => { if (e.target === lb) lb.style.display = 'none'; });
      let tsx = 0;
      lbImg.addEventListener('touchstart', e => { tsx = e.touches[0].clientX; }, { passive: true });
      lbImg.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - tsx;
        if (Math.abs(dx) < 40) return;
        this.lbIndex = dx < 0
          ? (this.lbIndex + 1) % this.lbImages.length
          : (this.lbIndex - 1 + this.lbImages.length) % this.lbImages.length;
        this.openLightbox();
      }, { passive: true });
    }
  }
}

/* --- VARIANT SELECTOR --- */
class VariantSelector {
  constructor(el) {
    this.el = el;
    this.productData = JSON.parse(el.querySelector('[data-product-json]')?.textContent || '{}');
    this.currentVariant = this.productData.variants?.[0];
    this.init();
  }
  init() {
    this.el.querySelectorAll('.variant-btn').forEach(btn => {
      btn.addEventListener('click', () => this.select(btn));
    });
  }
  select(btn) {
    const group = btn.closest('[data-option-group]');
    group?.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    this.updateVariant();
  }
  updateVariant() {
    const selected = {};
    this.el.querySelectorAll('[data-option-group]').forEach(group => {
      const name = group.dataset.optionGroup;
      const active = group.querySelector('.variant-btn.active');
      if (active) selected[name] = active.dataset.value;
    });
    const variant = this.productData.variants?.find(v =>
      v.options.every((opt, i) => {
        const key = this.productData.options[i];
        return !selected[key] || selected[key] === opt;
      })
    );
    if (variant) {
      this.currentVariant = variant;
      this.el.querySelector('[name="id"]').value = variant.id;
      const priceEl = this.el.querySelector('.product-info__price');
      const compareEl = this.el.querySelector('.product-info__compare');
      if (priceEl) priceEl.textContent = CartManager.formatMoney(variant.price);
      if (compareEl && variant.compare_at_price > variant.price) {
        compareEl.textContent = CartManager.formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else if (compareEl) {
        compareEl.style.display = 'none';
      }
      const atcBtn = this.el.querySelector('.atc-btn');
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        atcBtn.querySelector('span').textContent = variant.available ? 'Add to Bag' : 'Sold Out';
      }
      // Swap gallery image to the one assigned to this variant
      if (variant.featured_image) {
        const thumbIdx = variant.featured_image.position - 1;
        const thumb = this.el.querySelector(`.product-gallery__thumb[data-index="${thumbIdx}"]`);
        if (thumb) thumb.click();
      }
    }
  }
}

/* --- ACCORDION --- */
class Accordion {
  constructor(el) {
    el.querySelectorAll('.accordion-trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.accordion-item');
        const isOpen = item.classList.contains('open');
        el.querySelectorAll('.accordion-item').forEach(i => i.classList.remove('open'));
        if (!isOpen) item.classList.add('open');
      });
    });
  }
}

/* --- SCROLL ANIMATIONS --- */
class ScrollAnimations {
  constructor() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          this.observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.fade-in').forEach(el => this.observer.observe(el));
  }
}

/* --- ROUTINE TABS --- */
class RoutineTabs {
  constructor(el) {
    this.steps = el.querySelectorAll('.routine__step');
    this.visual = el.querySelector('.routine__visual img');
    this.badge = el.querySelector('.routine__visual-badge-product');
    this.init();
  }
  init() {
    this.steps.forEach((step, i) => {
      step.addEventListener('click', () => this.activate(i));
    });
    if (this.steps[0]) this.activate(0);
  }
  activate(index) {
    this.steps.forEach((s, i) => s.classList.toggle('active', i === index));
    const active = this.steps[index];
    const img = active.dataset.img;
    const product = active.dataset.product;
    if (img && this.visual) this.visual.src = img;
    if (product && this.badge) this.badge.textContent = product;
  }
}

/* --- NEWSLETTER FORM --- */
class NewsletterForm {
  constructor(el) {
    el.addEventListener('submit', async e => {
      e.preventDefault();
      const email = el.querySelector('input[type="email"]')?.value;
      if (!email) return;
      const btn = el.querySelector('button[type="submit"]');
      btn.textContent = '...';
      btn.disabled = true;
      await new Promise(r => setTimeout(r, 1000));
      toast.show('Welcome to Eighteen Wellness!', 'success');
      el.reset();
      btn.textContent = 'Subscribe';
      btn.disabled = false;
    });
  }
}

/* --- BEFORE / AFTER SLIDER --- */
class BeforeAfter {
  constructor(el) {
    this.el = el;
    this.before = el.querySelector('.ba-before');
    this.handle = el.querySelector('.ba-handle');
    this.dragging = false;
    this.init();
  }
  init() {
    this.el.addEventListener('mousedown', e => { this.dragging = true; this.move(e.clientX); });
    this.el.addEventListener('touchstart', e => { this.dragging = true; this.move(e.touches[0].clientX); }, { passive: true });
    window.addEventListener('mousemove', e => { if (this.dragging) this.move(e.clientX); });
    window.addEventListener('touchmove', e => { if (this.dragging) this.move(e.touches[0].clientX); }, { passive: true });
    window.addEventListener('mouseup', () => { this.dragging = false; });
    window.addEventListener('touchend', () => { this.dragging = false; });
  }
  move(clientX) {
    const rect = this.el.getBoundingClientRect();
    const pct = Math.min(Math.max(((clientX - rect.left) / rect.width) * 100, 2), 98);
    this.before.style.width = pct + '%';
    this.handle.style.left = pct + '%';
  }
}

/* --- CART UPSELL --- */
const UPSELL_MAP = {
  // Devices
  'ionic-scalp-massager':                  ['peptide-hair-growth-serum', 'hair-skin-nails-gummies'],
  'led-therapy-mask':                      ['retinol-and-peptide-face-serum', '10-niacinamide-serum'],
  'heated-vacuum-cupping':                 ['recovery-cream', 'magnesium-glycinate'],
  'body-sculpt-massager':                  ['recovery-cream', 'creatine-monohydrate'],
  // Skincare
  'peptide-hair-growth-serum':             ['ionic-scalp-massager', 'hair-skin-nails-gummies'],
  'retinol-and-peptide-face-serum':        ['led-therapy-mask', '10-niacinamide-serum'],
  '10-niacinamide-serum':                  ['led-therapy-mask', 'retinol-and-peptide-face-serum'],
  'recovery-cream':                        ['body-sculpt-massager', 'heated-vacuum-cupping'],
  // Supplements — hair & skin
  'hair-skin-nails-gummies':               ['ionic-scalp-massager', 'peptide-hair-growth-serum'],
  'glow-collagen-strips':                  ['led-therapy-mask', 'retinol-and-peptide-face-serum'],
  // Supplements — wellness
  'omega-3-epa-180mg-dha-120mg':           ['probiotic-40-billion-with-prebiotics', 'magnesium-glycinate'],
  'probiotic-40-billion-with-prebiotics':  ['ashwagandha', 'magnesium-glycinate'],
  'magnesium-glycinate':                   ['ashwagandha', 'omega-3-epa-180mg-dha-120mg'],
  'ashwagandha':                           ['magnesium-glycinate', 'beetroot'],
  'bone-support-strips':                   ['magnesium-glycinate', 'omega-3-epa-180mg-dha-120mg'],
  // Supplements — performance
  'beetroot':                              ['creatine-monohydrate', 'hydration-powder-lemonade'],
  'creatine-monohydrate':                  ['creatine-hydration-powder', 'beetroot'],
  'creatine-hydration-powder':             ['creatine-monohydrate', 'magnesium-glycinate'],
  'hydration-powder-lemonade':             ['creatine-hydration-powder', 'beetroot'],
};

const cartUpsell = {
  container: null,
  itemsEl: null,
  cache: {},

  init() {
    this.container = document.getElementById('cart-upsell');
    this.itemsEl = document.getElementById('cart-upsell-items');
  },

  async update(cartItems) {
    if (!this.container || !this.itemsEl) return;
    const cartHandles = cartItems.map(i => i.handle);

    const suggested = [];
    for (const handle of cartHandles) {
      for (const rec of (UPSELL_MAP[handle] || [])) {
        if (!cartHandles.includes(rec) && !suggested.includes(rec)) {
          suggested.push(rec);
          if (suggested.length >= 2) break;
        }
      }
      if (suggested.length >= 2) break;
    }

    if (!suggested.length) {
      this.container.style.display = 'none';
      return;
    }

    const products = (await Promise.all(suggested.map(h => this.fetchProduct(h)))).filter(Boolean);
    if (!products.length) { this.container.style.display = 'none'; return; }

    this.itemsEl.innerHTML = products.map(p => `
      <div class="cart-upsell-item">
        <img class="cart-upsell-item__img" src="${p.featured_image || ''}" alt="${p.title}" loading="lazy">
        <div class="cart-upsell-item__info">
          <div class="cart-upsell-item__name">${p.title}</div>
          <div class="cart-upsell-item__price">${CartManager.formatMoney(p.price)}</div>
        </div>
        <button class="cart-upsell-item__add" data-variant="${p.variants[0].id}">+ Add</button>
      </div>
    `).join('');

    this.itemsEl.querySelectorAll('.cart-upsell-item__add').forEach(btn => {
      btn.addEventListener('click', async () => {
        btn.textContent = '...';
        btn.disabled = true;
        try {
          await CartManager.addItem(btn.dataset.variant, 1);
          await CartDrawer.refresh();
          toast.show('Added to bag', 'success');
        } catch {
          toast.show('Could not add item', 'error');
          btn.textContent = '+ Add';
          btn.disabled = false;
        }
      });
    });

    this.container.style.display = 'block';
  },

  async fetchProduct(handle) {
    if (this.cache[handle]) return this.cache[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      this.cache[handle] = data;
      return data;
    } catch { return null; }
  },
};

/* --- PROMO COUNTDOWN --- */
function initPromoCountdown() {
  const el = document.getElementById('promo-countdown');
  if (!el) return;
  const deadline = new Date(el.dataset.deadline).getTime();
  if (isNaN(deadline)) return;
  let timer;
  const tick = () => {
    const diff = deadline - Date.now();
    if (diff <= 0) {
      el.textContent = '00:00:00';
      if (timer) clearInterval(timer);
      return;
    }
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    el.textContent = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };
  tick();
  timer = setInterval(tick, 1000);
}

/* --- INIT --- */
document.addEventListener('DOMContentLoaded', () => {
  initPromoCountdown();
  // Remove Judge.me duplicate microdata — we provide correct JSON-LD already
  document.querySelectorAll('.jdgm-rich-snippet').forEach(el => {
    el.removeAttribute('itemscope');
    el.removeAttribute('itemtype');
    el.removeAttribute('itemprop');
  });

  new Header();
  new ScrollAnimations();
  CartDrawer.init();
  cartUpsell.init();

  // Load Judge.me lazy images (data-src → src) and block supplier links
  document.querySelectorAll('.jdgm-rev__pic-img[data-src]').forEach(img => {
    img.src = img.dataset.src;
  });
  // Global lightbox close handler
  document.addEventListener('click', e => {
    if (e.target.id === 'img-lightbox-close' || e.target.id === 'img-lightbox') {
      const lb = document.getElementById('img-lightbox');
      if (!lb) return;
      lb.style.display = 'none';
      lb._rvMode = false;
    }
    // Open review photos in lightbox with swipe support
    const link = e.target.closest('a[href*="aliexpress"], a[href*="ae-pic"], a[href*="alicdn"]');
    if (!link) return;
    e.preventDefault();
    const clickedImg = link.querySelector('img') || e.target;
    const clickedSrc = clickedImg.src || clickedImg.dataset.src;
    if (!clickedSrc) return;
    const lb = document.getElementById('img-lightbox');
    const lbImg = document.getElementById('img-lightbox-img');
    if (!lb || !lbImg) return;
    // Collect all review photos
    const allImgs = Array.from(document.querySelectorAll('.jdgm-rev__pic-img')).map(i => i.src || i.dataset.src).filter(Boolean);
    let rvIdx = allImgs.indexOf(clickedSrc);
    if (rvIdx < 0) rvIdx = 0;
    const showRvPhoto = (idx) => {
      const src = allImgs[idx];
      lbImg.style.backgroundImage = 'url(' + src + ')';
      lbImg.style.backgroundColor = src.includes('.png') ? '#fff' : 'transparent';
      const dots = document.getElementById('img-lightbox-dots');
      if (dots) dots.innerHTML = allImgs.map((_, i) =>
        `<span style="width:7px;height:7px;border-radius:50%;background:${i === idx ? '#fff' : 'rgba(255,255,255,0.35)'};display:inline-block;margin:0 3px;"></span>`
      ).join('');
    };
    const prev = document.getElementById('img-lightbox-prev');
    const next = document.getElementById('img-lightbox-next');
    const dots = document.getElementById('img-lightbox-dots');
    if (prev) prev.style.display = allImgs.length > 1 ? '' : 'none';
    if (next) next.style.display = allImgs.length > 1 ? '' : 'none';
    if (dots) dots.style.display = allImgs.length > 1 ? '' : 'none';
    showRvPhoto(rvIdx);
    lb.style.display = 'block';
    lb._rvImages = allImgs;
    lb._rvIndex = rvIdx;
    lb._rvMode = true;
    if (!lb._rvReady) {
      lb._rvReady = true;
      prev?.addEventListener('click', () => { if (!lb._rvMode) return; lb._rvIndex = (lb._rvIndex - 1 + lb._rvImages.length) % lb._rvImages.length; showRvPhoto(lb._rvIndex); });
      next?.addEventListener('click', () => { if (!lb._rvMode) return; lb._rvIndex = (lb._rvIndex + 1) % lb._rvImages.length; showRvPhoto(lb._rvIndex); });
      let ts = 0;
      lbImg.addEventListener('touchstart', ev => { ts = ev.touches[0].clientX; }, { passive: true });
      lbImg.addEventListener('touchend', ev => {
        if (!lb._rvMode) return;
        const dx = ev.changedTouches[0].clientX - ts;
        if (Math.abs(dx) < 40) return;
        lb._rvIndex = dx < 0 ? (lb._rvIndex + 1) % lb._rvImages.length : (lb._rvIndex - 1 + lb._rvImages.length) % lb._rvImages.length;
        showRvPhoto(lb._rvIndex);
      }, { passive: true });
    }
  });

  document.querySelectorAll('[data-ba-slider]').forEach(el => new BeforeAfter(el));

  document.querySelectorAll('.product-form').forEach(form => new AddToCart(form));
  document.querySelectorAll('.product-section').forEach(el => {
    new VariantSelector(el);
    const gallery = el.querySelector('.product-gallery');
    if (gallery) new ProductGallery(gallery);
    new StickyATC();
  });
  document.querySelectorAll('.product-accordion').forEach(el => new Accordion(el));
  document.querySelectorAll('.routine__steps').forEach(el => new RoutineTabs(el.closest('[data-routine]') || el));
  document.querySelectorAll('.newsletter__form').forEach(el => new NewsletterForm(el));

  document.querySelectorAll('[data-routine]').forEach(el => new RoutineTabs(el));
});
